import {acceptHMRUpdate, defineStore} from 'pinia'
import {DefaultTheme} from 'src/classes/DefaultTheme.js'
import {computed, ref} from 'vue'
import axios from 'axios'
import {Quasar} from 'quasar'
import { logger } from 'src/boot/logger';

export const useAuthStore = defineStore('auth', () => {
  const loginname = ref('')
  const personID = ref('')
  const reloadedAtStartup = ref(false)  // boolean indicates if app reload once automatically cause app and client version number does not match)
  const queryKeyFromDeepLink = ref('')
  const queryAppKeyFromDeepLink = ref('')
  const queryKeySelectFirstRow = ref(false)
  const queryParamListFromDeepLink = ref([])
  const queryParamOverridePermanent = ref(false)
  const formularWhiteList = ref([])
  const autoLoginAsGuest = ref(false)
  const preventParameterInput = ref(false)
  const queryActionNativeNameFromDeepLink = ref('')
  const queryAppActionNativeNameFromDeepLink = ref('')
  const queryActionCxtParamFromDeepLink = ref('')
  const portalServerVersion = ref('')
  const tryAutoLogin = ref(true)
  const rootCategoryID = ref('')
  const authenticated = ref(false)
  const loginProcessing = ref(false)
  const autoLoginFailed = ref(false)
  const themeList = ref([])
  const queryExportOptions = ref({})
  const queryExportfilter = ref({})
  const sessionToken = ref('')
  const errorDetail = ref(false)
  const currentCultureID = ref('')
  const currentLanguage = ref('')
  const currentSetting = ref('')
  const cultureList = ref([])
  // Getter
  const siteID = computed(() => PP_CONST.SITE_ID)
  const activeTheme = computed(() => DefaultTheme)

  function authUser(userData) {
    loginname.value = userData.loginname
    personID.value = userData.personID
    authenticated.value = true
  }

  function clearAuthData() {
    localStorage.clear()
    tryAutoLogin.value = false
    loginname.value = null
    authenticated.value = false
    sessionToken.value = null
  }

  function setCulture(culture) {
    currentCultureID.value = culture.ID
    currentLanguage.value = culture.titleLocal
  }

  async function sessionCreateToken(authData) {
    loginProcessing.value = true
    try {
      const response = await axios.post('/sessionCreateToken', {
        loginname: authData.loginname,
        password: authData.password,
        siteID: siteID.value,
        appName: getAPPName()
      }, {
        withCredentials: true
      })
      if (response.data.token) {
        await sessionTouch()
        let tokendata = JSON.parse(atob(response.data.token.split('.')[1]))
        rootCategoryID.value = tokendata.rootCategoryID
        authUser({
          loginname: tokendata.userName,
          personID: tokendata.personID
        })
        loginProcessing.value = false
      }
    } catch (e) {
      loginProcessing.value = false
      console.warn('Error sessionCreateToken: ', e)
      throw e
    }
  }

  async function sessionCreateTokenStore(authData) {
    loginProcessing.value = true
    try {
      const resp = await axios.post('/sessionCreateTokenStore', {
        loginname: authData.loginname,
        password: authData.password,
        siteID: siteID.value,
        appName: getAPPName()
      }, {
        withCredentials: true
      })
      if (resp.data.token) {
        await sessionTouch()
        localStorage.setItem('storedRefreshToken', resp.data.token)
        let tokendata = JSON.parse(atob(resp.data.token.split('.')[1]))
        rootCategoryID.value = tokendata.rootCategoryID
        authUser({
          loginname: tokendata.userName,
          personID: tokendata.personID
        })
        loginProcessing.value = false
      }
    } catch (e) {
      loginProcessing.value = false
      console.warn('Error sessionCreateTokenStore: ', e)
      throw e
    }
  }

  async function performAutoLogin() {
    try {
      if ((autoLoginAsGuest.value && PP_CONST.APP_THEME[PP_CONST.APP_ACTIVE].AUTO_LOGIN_AS) || PP_CONST.APP_THEME[PP_CONST.APP_ACTIVE].ALWAYS_AUTOLOGIN) {
        // try login with perhaps existing refresh token - so user is logged as himself
        try {
          await loginWithExistingRefreshToken()
        } catch (e) {
          console.warn("performautoLogin: Existing token invalid", e)
        }

        logger.info("After loginWithExistingRefreshToken authenticated:", authenticated.value)

        if (!authenticated.value) {
          // No autologin with existing refreshToken
          // Autologin with gst=1 parameter
          let loginname = PP_CONST.APP_THEME[PP_CONST.APP_ACTIVE].AUTO_LOGIN_AS
          let pass = null

          await sessionCreateTokenStore({loginname: loginname.trim(), password: pass})
          await sessionTouch()
        }

      } else if (PP_CONST.APP_THEME[PP_CONST.APP_ACTIVE].SESSION_NEVER_EXPIRE ) { // && !storedLoginUserIsGuestUserWithoutGuestParameter()
        await loginWithExistingRefreshToken()

      } else {
        throw new Error("Autologin failed.")
      }

    } catch (e) {
      console.warn('Error sessionCreateTokenStore: ', e)
      throw e
    }
  }
/*
  function storedLoginUserIsGuestUserWithoutGuestParameter() {
    let result = false
    let token = localStorage.getItem('storedRefreshToken')
    if (token) {
      try {
        let tokendata = JSON.parse(atob(token.split('.')[1]))
        let loggedInUser = tokendata.userName
        if (!autoLoginAsGuest.value && loggedInUser === PP_CONST.APP_THEME[PP_CONST.APP_ACTIVE].AUTO_LOGIN_AS) {
          return true
        }
      } catch (e) {
        console.warn("Error parsing existing token data")
      }
    }
    logger.info("storedLoginUserIsGuestUserWithoutGuestParameter:", result)
    return result
  }
*/
  async function loginWithExistingRefreshToken() {
    if (!autoLoginFailed.value) {
      let token = localStorage.getItem('storedRefreshToken')

      if (token) {
        loginProcessing.value = true
        try {
          const res = await axios.post('/sessionCreateTokenAutologin', {
            token: token
          }, {
            withCredentials: true
          })
          if (res.data.token) {
            let tokendata = JSON.parse(atob(res.data.token.split('.')[1]))
            rootCategoryID.value = tokendata.rootCategoryID
            authUser({
              loginname: tokendata.userName,
              personID: tokendata.personID
            })
            await sessionTouch()
            loginProcessing.value = false
          }
        } catch (e) {
          loginProcessing.value = false
          autoLoginFailed.value = true
          console.warn('Error sessionCreate with existing refreshToken: ', e)
          throw e
        }
      }
    }
  }

  async function checkPwReset() {
    try {
      const res = await axios.post('/checkPwReset', {
        siteID: siteID.value
      })
      return res
    } catch (e) {
      console.warn('checkPwReset - Axios Error: ' + e + ', endpoint: ', axios.defaults.baseURL)
      throw e
    }
  }

  async function personPwReset(username) {
    try {
      await axios.post('/personPwReset', {
        siteID: siteID.value,
        loginname: username
      })
    } catch (e) {
      console.warn('personPwReset Error: ' + e.response.data.sErrorMsg + ', iResult: ' + e.response.data.iResult)
      throw e
    }
  }

  async function cultureSet(cultureID) {
    try {
      await axios.post('/cultureSet', {
        siteID: siteID.value,
        cultureID: cultureID,
        sessionToken: sessionToken.value
      })
    } catch (e) {
      console.warn(
        'cultureSet Error: ' + e.response.data.sErrorMsg + ', iResult: ' + e.response.data.iResult)
      throw e
    }
  }

  async function cultureListGet() {
    try {
      const response = await axios.post('/cultureListGet', {
        siteID: siteID.value
      })
      cultureList.value = response.data.culture
      let cult = {}
      let lsLocale = localStorage.getItem('userLocale')
      let browserlocale = Quasar.lang.getLocale()
      if (lsLocale) {
        cult = response.data.culture.find(c => c.titleLocal == lsLocale)
      } else if (browserlocale) {
        switch (browserlocale) {
          case 'en-us':
            cult = response.data.culture.find(c => c.titleLocal == 'English US')
            break
          case 'en-uk':
            cult = response.data.culture.find(c => c.titleLocal == 'English UK')
            break
          case 'en':
            cult = response.data.culture.find(c => c.titleLocal == 'English UK')
            break
          default:
            cult = response.data.culture.find(c => c.titleLocal == 'Österreich')
            break
        }
      }
      if (!cult) {
        cult = res.data.culture.find(c => c.localDefault)
      }
      if (cult) {
        setCulture(cult)
      }
      return currentLanguage.value
    } catch (e) {
      console.warn(
        'cultureListGet Error: ' + e.response.data.sErrorMsg + ', iResult: ' + e.response.data.iResult)
      throw e
    }
  }

  async function appVersionOnServerGet() {
    try {
      let resp = await axios.post('/versionGet?appName=' + getAPPName())
      return resp
    } catch (error) {
      console.warn('sessionCreate - Axios Error: ' + error + ', endpoint: ', axios.defaults.baseURL)
      return error
    }
  }

  async function sessionTouch() {
    const tokenNotExpired = checkSessionTokenExpiryDate(sessionToken.value)
    if (tokenNotExpired) {
      return sessionToken.value
    } else {
      try {
        const response = await axios.post('/getSessionToken', {
          withCredentials: true
        })
        sessionToken.value = response.data.token
        return response.data.token
      } catch (e) {
        // console.error('Error getSessionToken: ' + error + ', endpoint: ', axios.defaults.baseURL)
        let myError = new Error('Session ungültig oder abgelaufen.')
        myError.response = {data: {iResult: -1}}
        myError.jwtError = true
        return myError
      }
    }
  }

  function checkSessionTokenExpiryDate(token) {
    if (!!token) {
      const currentDate = (
        Math.floor((
          Date.now()
        ).valueOf() / 1000 + 150)
      ) // 2:30 minutes offset to make sure there is no overlap
      const expDate = Number((
        JSON.parse(atob(token.split('.')[1]))
      ).exp)
      return currentDate < expDate
    } else {
      return false
    }
  }

  async function changePassword(oldPassword, newPassword) {
    try {
      await sessionTouch()
      await axios.post('/changePassword', {
        "sessionToken": sessionToken.value,
        "siteID": siteID.value,
        "loginname": loginname.value,
        "personID": personID.value,
        "password": oldPassword,
        "newPassword": newPassword
      })
    } catch (e) {
      console.error('changePassword error: ', e)
      throw e
    }
  }

  async function logout() {
    try {
      await sessionTouch()
      await axios.post('/sessionClose', {
        sessionToken: sessionToken.value
      })
      await axios.post('/logout', {
        withCredentials: true
      })
      clearAuthData()
    } catch (e) {
      console.warn('sessionClose - Axios Error: ' + e + ', endpoint: ', axios.defaults.baseURL)
      throw e
    }
  }

  async function sitePropertyListGet(theme) {
    try {
      await sessionTouch()
      const response = await axios.post('/sitePropertyListGet', {
        sessionToken: sessionToken.value,
        siteID: siteID.value,
        theme: theme
      })
      return response.data
    } catch (e) {
      console.warn('sitePropertyListGet - Axios Error: ' + e + ', endpoint: ', axios.defaults.baseURL)
      throw e
    }
  }

  return {
    loginname,
    personID,
    reloadedAtStartup,
    queryKeyFromDeepLink,
    queryAppKeyFromDeepLink,
    queryKeySelectFirstRow,
    queryParamListFromDeepLink,
    queryParamOverridePermanent,
    formularWhiteList,
    autoLoginAsGuest,
    preventParameterInput,
    queryActionNativeNameFromDeepLink,
    queryAppActionNativeNameFromDeepLink,
    queryActionCxtParamFromDeepLink,
    portalServerVersion,
    tryAutoLogin,
    rootCategoryID,
    authenticated,
    loginProcessing,
    autoLoginFailed,
    themeList,
    activeTheme,
    queryExportOptions,
    queryExportfilter,
    sessionToken,
    errorDetail,
    currentCultureID,
    currentLanguage,
    currentSetting,
    cultureList,
    siteID,

    clearAuthData,
    sessionCreateToken,
    sessionCreateTokenStore,
    performAutoLogin,
    checkPwReset,
    personPwReset,
    cultureSet,
    cultureListGet,
    appVersionOnServerGet,
    sessionTouch,
    changePassword,
    logout,
    sitePropertyListGet
  }
})

// Enable Hot Module Replacement for stores. make sure to pass the right store definition, `useFormStore` in this case.
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
